'use client';

import React, { useEffect } from 'react';

import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { checkAuth } from './redux/actions/authActions';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';
import AdminLayout from './components/layouts/AdminLayout';

//ErrorBoundary
import ErrorBoundary from './components/common/ErrorBoundary';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword.js';
import VerifyEmail from './pages/auth/VerifyEmail';

// User Pages
import Dashboard from './pages/Dashboard';
import SubmissionForm from './pages/submissions/SubmissionForm';
import SubmissionsList from './pages/submissions/SubmissionsList';
import SubmissionDetails from './pages/submissions/SubmissionDetails';
import OrdersList from './pages/orders/OrdersList';
import OrderDetails from './pages/orders/OrderDetails';
import Profile from './pages/Profile';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import UsersList from './pages/admin/UsersList';
import UserForm from './pages/admin/UserForm';
import AdminSubmissions from './pages/admin/AdminSubmissions';
import AdminOrders from './pages/admin/AdminOrders.js';
import InvitationsList from './pages/admin/InvitationsList';
import AlertSettings from './pages/admin/AlertSettings';
import FileNamingSettings from './pages/admin/FileNamingSettings';

// Error Pages
import NotFound from './pages/errors/NotFound';

//Notifications
import Notifications from './pages/Notifications';

//Websocket
import websocketService from './services/websocketService';

// Protected Route Component
const ProtectedRoute = ({ children, isAdmin = false }) => {
  const { isAuthenticated, user, loading } = useSelector(state => state.auth);

  if (loading) {
    return <div>Chargement...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (isAdmin && user?.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const App = () => {
  const { isAuthenticated } = useSelector(state => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    // Vérifier si l'utilisateur est déjà authentifié (token stocké)
    dispatch(checkAuth());
  }, [dispatch]);

  // Initialisation du WebSocket lorsque l'utilisateur est authentifié
  useEffect(() => {
    if (isAuthenticated) {
      websocketService.initWebsocket();
    }

    return () => {
      websocketService.disconnectWebsocket();
    };
  }, [isAuthenticated]);

  return (
    <Routes>
      {/* Routes d'authentification */}
      <Route path="/" element={<AuthLayout />}>
        <Route index element={<Navigate to="/login" />} />
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="verify-email" element={<VerifyEmail />} />
      </Route>

      {/* Routes utilisateur */}
      <Route path="/" element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="submissions">
          <Route index element={<SubmissionsList />} />
          <Route path="new" element={<SubmissionForm />} />
          <Route path=":id" element={<SubmissionDetails />} />
          <Route path=":id/edit" element={<SubmissionForm />} />
        </Route>
        <Route path="orders">
          <Route index element={<OrdersList />} />
          <Route path=":id" element={<OrderDetails />} />
        </Route>
        <Route path="profile" element={<Profile />} />
      </Route>

      {/* Routes administrateur */}
      <Route path="/admin" element={
        <ProtectedRoute isAdmin={true}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="users">
          <Route index element={<UsersList />} />
          <Route path="new" element={<UserForm />} />
          <Route path=":id" element={
            <ErrorBoundary>
              <UserForm />
            </ErrorBoundary>
          } />
          <Route path="invitations" element={<InvitationsList />} />
        </Route>
        <Route path="submissions">
          <Route index element={<AdminSubmissions />} />
          <Route path=":id" element={<SubmissionDetails />} />
          <Route path=":id/edit" element={<SubmissionForm />} />
        </Route>
        <Route path="orders">
          <Route index element={<AdminOrders />} />
          <Route path=":id" element={<OrderDetails />} />
        </Route>
        <Route path="settings/alerts" element={<AlertSettings />} />
        <Route path="settings/filenames" element={<FileNamingSettings />} />
      </Route>

      {/* Route d'erreur 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default App;